import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import type { OperationState } from "@/features/operations/types";

import { useAuthStore } from "./authStore";

/** "pending" is a client-only state before the first status poll resolves. */
export type TrackedStatus = "pending" | OperationState;

export interface TrackedOperation {
  /** Temporal workflow id returned by the create call. */
  workflowId: string;
  /** Customer-facing resource name, e.g. "web-1". */
  label: string;
  /** Resource family, for an icon/context in the panel. */
  kind:
    | "instance"
    | "volume"
    | "loadbalancer"
    | "snapshot"
    | "cluster"
    | "function"
    | "database"
    | "other";
  /** A create with no workflow behind it (a managed database): its progress is
   *  read from the resource itself. */
  resource?: { type: "database"; id: string };
  status: TrackedStatus;
  /** Epoch ms when tracking began. */
  startedAt: number;
  resultId?: string | null;
  /** Human-readable failure reason (backend `error` field, or a client-side
   *  reason like "status unavailable"). */
  error?: string | null;
}

const TERMINAL: ReadonlySet<TrackedStatus> = new Set([
  "completed",
  "failed",
  "canceled",
  "terminated",
  "timed_out",
]);

/** Stop polling an operation that has been in-flight this long (safety net). */
const MAX_TRACK_MS = 30 * 60 * 1000;
const MAX_OPERATIONS = 50;

export function isTerminal(status: TrackedStatus): boolean {
  return TERMINAL.has(status);
}

export function isInFlight(op: TrackedOperation): boolean {
  return !isTerminal(op.status) && Date.now() - op.startedAt < MAX_TRACK_MS;
}

interface OperationsState {
  operations: TrackedOperation[];
  /** The account the tracked operations belong to (see `adoptOwner`). */
  ownerId: string | null;
  track: (op: Omit<TrackedOperation, "status" | "startedAt">) => void;
  setStatus: (
    workflowId: string,
    status: OperationState,
    resultId?: string | null,
    error?: string | null,
  ) => void;
  remove: (workflowId: string) => void;
  clearFinished: () => void;
  /** Flip non-terminal operations older than MAX_TRACK_MS to timed_out, so a
   *  poller that died (tab closed mid-op, backend gone) can't leave a
   *  "pending" ghost forever. Called periodically by OperationsTracker. */
  markStale: () => void;
}

export const useOperationsStore = create<OperationsState>()(
  persist(
    (set) => ({
      operations: [],
      ownerId: null,
      track: (op) =>
        set((state) => {
          const rest = state.operations.filter(
            (o) => o.workflowId !== op.workflowId,
          );
          const next: TrackedOperation = {
            ...op,
            status: "pending",
            startedAt: Date.now(),
          };
          return { operations: [next, ...rest].slice(0, MAX_OPERATIONS) };
        }),
      setStatus: (workflowId, status, resultId, error) =>
        set((state) => ({
          operations: state.operations.map((o) =>
            o.workflowId === workflowId
              ? {
                  ...o,
                  status,
                  resultId: resultId ?? o.resultId ?? null,
                  error: error ?? o.error ?? null,
                }
              : o,
          ),
        })),
      remove: (workflowId) =>
        set((state) => ({
          operations: state.operations.filter(
            (o) => o.workflowId !== workflowId,
          ),
        })),
      clearFinished: () =>
        set((state) => ({
          operations: state.operations.filter((o) => isInFlight(o)),
        })),
      markStale: () =>
        set((state) => {
          if (
            !state.operations.some(
              (o) =>
                !isTerminal(o.status) &&
                Date.now() - o.startedAt >= MAX_TRACK_MS,
            )
          ) {
            return state;
          }
          return {
            operations: state.operations.map((o) =>
              !isTerminal(o.status) && Date.now() - o.startedAt >= MAX_TRACK_MS
                ? {
                    ...o,
                    status: "timed_out" as const,
                    error: o.error ?? "no result after 30 minutes",
                  }
                : o,
            ),
          };
        }),
    }),
    {
      name: "cloudpanel.operations",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/**
 * The list is kept in this browser, not on the server, under one key — so it
 * used to outlive the session: the next account to sign in on the same browser
 * was shown the previous account's activity ("Creating tes — Instance · 7d
 * ago" on a brand-new account). The list now belongs to one account and is
 * emptied when a different one signs in; signing out and back in as the same
 * account keeps it. A list stored before this had no owner and is dropped once.
 */
export function adoptOwner(userId: string | null | undefined): void {
  if (!userId) return; // signed out: nothing is shown, nothing to decide yet
  if (useOperationsStore.getState().ownerId === userId) return;
  useOperationsStore.setState({ operations: [], ownerId: userId });
}

adoptOwner(useAuthStore.getState().user?.id);
useAuthStore.subscribe((state) => adoptOwner(state.user?.id));
